import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { Store } from '@ngrx/store';
import {
	DatabaseLoadingState,
	databases,
	databasesLoadingState
} from '@store/reducers/databases.reducer';
import { combineLatest, filter, map, Observable, take } from 'rxjs';
import { Database } from '../../../definitions/database';
import { DatabasesService } from '../services/databases.service';

/**
 * Resolver function for loading databases associated with a specific application.
 *
 * This resolver:
 * - Triggers database loading via the DatabasesService
 * - Returns databases from the store once loaded (even if empty array)
 * - Uses the loading state signal to determine when data is ready
 *
 * @param route - The activated route snapshot containing route parameters
 * @returns Observable of Database array from the store (can be empty)
 *
 * @example
 * // Route configuration
 * {
 *   path: 'app/:appId/databases',
 *   resolve: { databases: databasesResolverFn },
 *   component: DatabaseListComponent
 * }
 */
export const databasesResolverFn: ResolveFn<Array<Database>> = (
	_route: ActivatedRouteSnapshot
): Observable<Array<Database>> => {
	const databasesSvc = inject(DatabasesService);
	const store = inject(Store);

	// Trigger load (will only dispatch if not already loading/loaded)
	databasesSvc.loadDatabases();

	// Wait for loading state to become 'loaded', then return databases
	return combineLatest({
		state: toObservable(databasesLoadingState),
		databases: store.select(databases)
	}).pipe(
		filter(({ state }) => state === DatabaseLoadingState.Loaded),
		map(({ databases }) => databases),
		take(1)
	);
};
